import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useSquadre } from "../../hooks/useFase7";
import { MobileShell } from "../shell/MobileShell";
import { FULL_BY_GROUP } from "../common/ruoliColors";
import { RUOLI_MANTRA_ORDER, ruoloMantraClass } from "../common/ruoliMantra";
import { NOME_MODALITA_PRO, siglaRuolo } from "../common/nomenclatura";
import { tr } from "../../i18n";

type RuoloClassic = "P" | "D" | "C" | "A";
type Stato = "serie_a" | "all_estero" | "ritirato" | "deceduto" | "retrocesso";

interface Giocatore {
  id: string;
  nome: string;
  club_serie_a: string | null;
  ruolo_classic: RuoloClassic | null;
  ruoli_mantra: string[] | null;
  stato: Stato;
  eta: number | null;
  transfermarkt_id: string | null;
  transfermarkt_url?: string | null;
  posseduto_da_squadra_id: string | null;
  valore_tm_milioni: string | null;
  valore_tm_data: string | null;
}

interface GiocatoriResponse {
  items: Giocatore[];
  total: number;
  limit: number;
  offset: number;
}

// Andrea 2026-05-22: palette ufficiale FantaSquad — usa FULL_BY_GROUP
// dalla mappa centralizzata `ruoliColors.ts`.
const RUOLO_COLOR: Record<RuoloClassic, string> = {
  P: FULL_BY_GROUP.Por,
  D: FULL_BY_GROUP.Def,
  C: FULL_BY_GROUP.Mid,
  A: FULL_BY_GROUP.Att,
};

const RUOLO_LABEL: Record<RuoloClassic, string> = {
  P: "Por",
  D: "Dif",
  C: "Cen",
  A: "Att",
};

const PAGE_SIZE = 50;

type SortKey = "nome" | "ruolo" | "club" | "tm" | "eta" | "eta_desc";

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "nome", label: tr("Nome (A–Z)") },
  { value: "ruolo", label: tr("Ruolo (P→D→C→A)") },
  { value: "club", label: tr("Club Serie A") },
  { value: "tm", label: tr("Valore TM ↓") },
  { value: "eta", label: tr("Età ↑ (giovani prima)") },
  { value: "eta_desc", label: tr("Età ↓ (esperti prima)") },
];

export function GiocatoriPage() {
  const [filterRuolo, setFilterRuolo] = useState<RuoloClassic | "tutti">("tutti");
  // Multi-select: più ruoli mantra insieme (es. W+T). Vuoto = tutti.
  const [filterMantra, setFilterMantra] = useState<string[]>([]);
  const [filterStato, setFilterStato] = useState<"serie_a" | "fuori" | "tutti">("serie_a");
  const [filterClub, setFilterClub] = useState("");
  const [search, setSearch] = useState("");
  const [onlySvincolati, setOnlySvincolati] = useState(false);
  const [sortBy, setSortBy] = useState<SortKey>("nome");
  const [offset, setOffset] = useState(0);
  const [downloading, setDownloading] = useState<"pdf" | "xlsx" | null>(null);

  const { data: squadre } = useSquadre();
  const squadraMap = new Map(squadre?.map((s) => [s.id, s.nome]) ?? []);

  // Club SA per il filtro a tendina (dal listone, cambia solo a mercato)
  const { data: clubs } = useQuery({
    queryKey: ["giocatori-clubs"],
    queryFn: async () => (await api.get<string[]>("/giocatori/clubs")).data,
    staleTime: 60 * 60 * 1000,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "giocatori",
      filterRuolo,
      filterMantra.join(","),
      filterStato,
      filterClub,
      search,
      onlySvincolati,
      sortBy,
      offset,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("stato", filterStato);
      if (filterRuolo !== "tutti") params.set("ruolo_classic", filterRuolo);
      if (filterMantra.length > 0) params.set("ruolo_mantra", filterMantra.join(","));
      if (filterClub) params.set("club_serie_a", filterClub);
      if (search) params.set("search", search);
      if (onlySvincolati) params.set("only_svincolati", "true");
      params.set("sort", sortBy);
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(offset));
      const res = await api.get<GiocatoriResponse>(`/giocatori?${params}`);
      return res.data;
    },
  });

  // Reset offset quando cambia un filtro
  const resetAndSet =
    <T,>(setter: (v: T) => void) =>
    (v: T) => {
      setter(v);
      setOffset(0);
    };

  // Toggle di una pill mantra (multi-select)
  const toggleMantra = (r: string) => {
    setFilterMantra((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));
    setOffset(0);
  };

  // Scarica la lista corrente (stessi filtri, senza paginazione).
  // Andrea 2026-09-09: oltre al PDF serve l'Excel, per lavorarci sopra
  // (l'asta si prepara sul foglio elettronico).
  const scarica = async (formato: "pdf" | "xlsx") => {
    setDownloading(formato);
    try {
      const params = new URLSearchParams();
      params.set("stato", filterStato);
      if (filterRuolo !== "tutti") params.set("ruolo_classic", filterRuolo);
      if (filterMantra.length > 0) params.set("ruolo_mantra", filterMantra.join(","));
      if (filterClub) params.set("club_serie_a", filterClub);
      if (search) params.set("search", search);
      if (onlySvincolati) params.set("only_svincolati", "true");
      params.set("sort", sortBy);
      const res = await api.get(`/giocatori/${formato}?${params}`, {
        responseType: "blob",
      });
      const mime =
        formato === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `catalogo_giocatori_fantasquad.${formato}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <MobileShell logoSuffix="GIOCATORI">
      <h2 className="font-display text-2xl md:text-3xl text-accent tracking-wide">
        {tr("Catalogo giocatori")}
      </h2>

      {/* Filtri */}
      <div className="bg-bg-card border border-primary/20 rounded-card p-3 space-y-3">
        {/* Search */}
        <input
          type="text"
          placeholder={tr("Cerca per nome…")}
          value={search}
          onChange={(e) => resetAndSet(setSearch)(e.target.value)}
          className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm"
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {/* Filtro club a tendina */}
          <select
            value={filterClub}
            onChange={(e) => resetAndSet(setFilterClub)(e.target.value)}
            className="px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm"
            aria-label={tr("Filtra per club Serie A")}
          >
            <option value="">{tr("Tutti i club")}</option>
            {clubs?.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          {/* Ordinamento */}
          <select
            value={sortBy}
            onChange={(e) => resetAndSet(setSortBy)(e.target.value as SortKey)}
            className="px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm"
            aria-label={tr("Ordina per")}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                Ordina: {o.label}
              </option>
            ))}
          </select>

          {/* Toggle svincolati */}
          <label className="flex items-center gap-2 cursor-pointer text-sm bg-primary-deep border border-primary-soft rounded-lg px-3 py-2">
            <input
              type="checkbox"
              checked={onlySvincolati}
              onChange={(e) => resetAndSet(setOnlySvincolati)(e.target.checked)}
            />
            <span className={onlySvincolati ? "text-accent font-bold" : "text-fg"}>
              {tr("Solo svincolati")}
            </span>
          </label>
        </div>

        {/* Ruoli: classic + mantra sulla stessa riga.
            Mantra è multi-select: più pill accese = "almeno uno di questi"
            (es. W+T per tutti gli esterni offensivi). */}
        <div className="space-y-1">
          <div className="text-[10px] text-fg-muted uppercase tracking-wider">
            Ruoli{" "}
            <span className="normal-case tracking-normal">
              · sui ruoli {NOME_MODALITA_PRO} puoi selezionarne più di uno
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-1">
            <button
              type="button"
              onClick={() => {
                setFilterRuolo("tutti");
                setFilterMantra([]);
                setOffset(0);
              }}
              className={`px-2 py-1 rounded text-[11px] font-bold border transition ${
                filterRuolo === "tutti" && filterMantra.length === 0
                  ? "bg-accent text-ink border-accent"
                  : "bg-primary-deep text-fg-muted border-primary-soft"
              }`}
            >
              {tr("Tutti")}
            </button>
            {(["P", "D", "C", "A"] as const).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => resetAndSet(setFilterRuolo)(filterRuolo === r ? "tutti" : r)}
                className={`px-2 py-1 rounded text-[11px] font-bold transition ${
                  filterRuolo === r
                    ? "bg-accent text-ink"
                    : "bg-primary-deep text-fg-muted border border-primary-soft"
                }`}
              >
                {RUOLO_LABEL[r]}
              </button>
            ))}
            <span className="mx-1.5 h-5 w-px bg-primary-soft" aria-hidden />
            {RUOLI_MANTRA_ORDER.map((r) => {
              const active = filterMantra.includes(r);
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => toggleMantra(r)}
                  className={`px-2 py-1 rounded text-[10px] font-bold font-mono transition ${
                    active
                      ? `${ruoloMantraClass(r)} ring-2 ring-accent`
                      : `${ruoloMantraClass(r)} opacity-60 hover:opacity-100`
                  }`}
                >
                  {siglaRuolo(r)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Filtro stato Serie A / fuori (Andrea 2026-08-26) */}
        <div className="space-y-1">
          <div className="text-[10px] text-fg-muted uppercase tracking-wider">{tr("Stato")}</div>
          <div className="flex gap-1">
            {(
              [
                { v: "serie_a", label: tr("In Serie A") },
                { v: "fuori", label: tr("Fuori SA *") },
                { v: "tutti", label: tr("Tutti") },
              ] as const
            ).map(({ v, label }) => (
              <button
                key={v}
                type="button"
                onClick={() => resetAndSet(setFilterStato)(v)}
                className={`px-3 py-1 rounded text-xs font-bold transition ${
                  filterStato === v
                    ? v === "fuori"
                      ? "bg-warning text-ink"
                      : "bg-accent text-ink"
                    : "bg-primary-deep text-fg-muted border border-primary-soft"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stats + scarica PDF */}
      {data && (
        <div className="flex items-center justify-between gap-2 text-sm text-fg-muted">
          <div>
            <strong className="text-fg">{data.total}</strong> giocatori trovati
            {onlySvincolati && tr(" (svincolati)")}
            <span className="ml-2 text-warning text-xs">* = fuori Serie A</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline text-xs">
              Mostrando {Math.min(offset + 1, data.total)}–
              {Math.min(offset + PAGE_SIZE, data.total)}
            </span>
            <button
              type="button"
              onClick={() => scarica("pdf")}
              disabled={downloading !== null || data.total === 0}
              title={tr("Scarica la lista filtrata in PDF (ruoli, età, valori TM)")}
              className="px-3 py-1.5 rounded-lg text-xs font-bold bg-accent text-ink hover:opacity-90 disabled:opacity-40 transition whitespace-nowrap"
            >
              {downloading === "pdf" ? tr("Genero…") : tr("⬇ Scarica PDF")}
            </button>
            <button
              type="button"
              onClick={() => scarica("xlsx")}
              disabled={downloading !== null || data.total === 0}
              title={tr("Scarica la lista filtrata in Excel (già filtrabile e ordinabile)")}
              className="px-3 py-1.5 rounded-lg text-xs font-bold bg-success text-white hover:opacity-90 disabled:opacity-40 transition whitespace-nowrap"
            >
              {downloading === "xlsx" ? tr("Genero…") : tr("⬇ Scarica Excel")}
            </button>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="bg-bg-card border border-primary/20 rounded-card p-6 text-center text-fg-muted">
          {tr("Caricamento…")}
        </div>
      )}

      {error && (
        <div className="bg-danger/10 border border-danger/40 text-danger rounded-card p-3 text-sm">
          Errore: {String(error)}
        </div>
      )}

      {data && data.items.length === 0 && (
        <div className="bg-bg-card border border-primary/20 rounded-card p-6 text-center text-fg-muted">
          {tr("Nessun giocatore trovato per i filtri impostati.")}
        </div>
      )}

      {data && data.items.length > 0 && (
        <div className="bg-bg-card border border-primary/20 rounded-card p-0 overflow-hidden">
          {/* Header tabella desktop:
              Ruolo | Mantra | Nome | Età | Valore TM + Club SA | Posseduto da */}
          <div className="hidden md:grid grid-cols-[3rem_8rem_1fr_3rem_9rem_9rem] gap-3 px-4 py-2 bg-primary-deep/40 text-[10px] text-fg-muted uppercase tracking-wider font-mono border-b border-primary/20">
            <div>{tr("Ruolo")}</div>
            <div>{NOME_MODALITA_PRO}</div>
            <div>{tr("Nome")}</div>
            <div className="text-right">{tr("Età")}</div>
            <div className="text-right">{tr("Valore TM · Club")}</div>
            <div>{tr("Posseduto da")}</div>
          </div>

          {data.items.map((g) => (
            <Link
              key={g.id}
              to={`/giocatori/${g.id}`}
              className="grid grid-cols-[3rem_1fr_auto] md:grid-cols-[3rem_8rem_1fr_3rem_9rem_9rem] gap-3 items-center px-4 py-2.5 border-b border-primary/10 last:border-b-0 text-sm hover:bg-bg-card-hover transition"
            >
              {/* Ruolo classic badge */}
              <div>
                {g.ruolo_classic && (
                  <span
                    className={`font-mono font-bold px-2 py-0.5 rounded text-xs ${RUOLO_COLOR[g.ruolo_classic]}`}
                  >
                    {RUOLO_LABEL[g.ruolo_classic]}
                  </span>
                )}
              </div>

              {/* Mantra badges colorati (solo desktop, alla destra del ruolo) */}
              <div className="hidden md:flex flex-wrap gap-1">
                {g.ruoli_mantra?.map((r) => (
                  <span
                    key={r}
                    className={`font-mono font-bold text-[10px] px-1.5 py-0.5 rounded ${ruoloMantraClass(r)}`}
                  >
                    {siglaRuolo(r)}
                  </span>
                ))}
              </div>

              {/* Nome (mobile: anche mantra + valore TM sotto) */}
              <div className="min-w-0">
                <div className="font-bold text-fg truncate">
                  {g.nome}
                  {g.stato !== "serie_a" && (
                    <span
                      className="ml-1 text-warning"
                      title={tr("Fuori Serie A — non più nel listone attuale")}
                    >
                      *
                    </span>
                  )}
                </div>
                {/* Mobile: ruoli mantra colorati sotto al nome */}
                {g.ruoli_mantra && g.ruoli_mantra.length > 0 && (
                  <div className="md:hidden flex flex-wrap gap-1 mt-1">
                    {g.ruoli_mantra.map((r) => (
                      <span
                        key={r}
                        className={`font-mono font-bold text-[9px] px-1.5 py-0.5 rounded ${ruoloMantraClass(r)}`}
                      >
                        {siglaRuolo(r)}
                      </span>
                    ))}
                  </div>
                )}
                <div className="md:hidden text-xs text-fg-muted truncate mt-0.5">
                  {g.club_serie_a && <span>{g.club_serie_a}</span>}
                  {g.eta != null && <span className="ml-2">{g.eta} anni</span>}
                  {g.valore_tm_milioni && (
                    <span className="ml-2 text-accent font-mono">
                      {Number(g.valore_tm_milioni).toFixed(1)}M€
                    </span>
                  )}
                </div>
              </div>

              {/* Desktop: età */}
              <div className="hidden md:block text-right text-sm font-mono text-fg">
                {g.eta ?? "—"}
              </div>

              {/* Mobile: posseduto/svincolato compact */}
              <div className="md:hidden text-right text-xs">
                {g.posseduto_da_squadra_id ? (
                  <span className="text-success">
                    {squadraMap.get(g.posseduto_da_squadra_id) ?? "—"}
                  </span>
                ) : (
                  <span className="text-accent font-bold">SVINC.</span>
                )}
              </div>

              {/* Desktop: valore TM (in alto) + club SA (in basso) raggruppati */}
              <div className="hidden md:block text-right text-xs">
                {g.valore_tm_milioni ? (
                  <div className="text-accent font-bold font-mono">
                    {Number(g.valore_tm_milioni).toFixed(1)}M€
                  </div>
                ) : (
                  <div className="text-fg-muted">—</div>
                )}
                <div className="text-fg-muted text-[10px] truncate">{g.club_serie_a ?? "—"}</div>
              </div>

              {/* Desktop: posseduto */}
              <div className="hidden md:block text-xs">
                {g.posseduto_da_squadra_id ? (
                  <span className="text-success truncate block">
                    {squadraMap.get(g.posseduto_da_squadra_id) ?? "—"}
                  </span>
                ) : (
                  <span className="text-accent font-bold">{tr("SVINCOLATO")}</span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Paginazione */}
      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            className="btn-ghost text-sm disabled:opacity-30"
          >
            {tr("← Precedenti")}
          </button>
          <span className="text-xs text-fg-muted px-2">
            Pagina {Math.floor(offset / PAGE_SIZE) + 1} / {Math.ceil(data.total / PAGE_SIZE)}
          </span>
          <button
            type="button"
            disabled={offset + PAGE_SIZE >= data.total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            className="btn-ghost text-sm disabled:opacity-30"
          >
            {tr("Successivi →")}
          </button>
        </div>
      )}
    </MobileShell>
  );
}
